import { useMemo } from 'react';

import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  TypePolicy,
  from,
  createHttpLink,
  split,
} from '@apollo/client';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { onError } from '@apollo/client/link/error';
import { useTranslation } from 'react-i18next';

import { Query as EIP721Query } from './eip721-subgraph/types';
import { Query as LendingSeedQuery } from './lending-seed-subgraph/types';
import { Query as LendingSproutQuery } from './lending-sprout-subgraph/types';
import { Query as LendingHarvestQuery } from './lending-harvest-subgraph/types';
import { getEnv } from '~/env';

const getSubgraphHeaders = (): Record<string, string> | undefined => {
  const apiKey = getEnv('NEXT_PUBLIC_SUBGRAPH_API_KEY');
  return apiKey ? { Authorization: `Bearer ${apiKey}` } : undefined;
};

const customFieldPolicy: Required<TypePolicy>['fields'][string] = {
  keyArgs: (args, context) => {
    return (context.field?.alias?.value || '') + JSON.stringify(context.variables);
  },
};

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        owner: customFieldPolicy,
        depositeds: customFieldPolicy,
      } as Record<
        keyof EIP721Query &
          LendingSeedQuery &
          LendingSproutQuery &
          LendingHarvestQuery /** & HolderQuery */,
        Required<TypePolicy>['fields'][string]
      >,
    },
  },
});

export const withApolloClient = (ChildComponent) => {
  const WithApolloClientHOC = (props) => {
    const { i18n } = useTranslation();
    const client = useMemo(() => {
      const eip721Link = createHttpLink({
        headers: getSubgraphHeaders(),
        uri: getEnv('NEXT_PUBLIC_API_EIP721_SUBGRAPH_SERVER'),
      });

      const lendingSeedSubgraphLink = createHttpLink({
        headers: getSubgraphHeaders(),
        uri: getEnv('NEXT_PUBLIC_API_LENDING_SEED_SUBGRAPH_SERVER'),
      });

      const lendingSproutSubgraphLink = createHttpLink({
        headers: getSubgraphHeaders(),
        uri: getEnv('NEXT_PUBLIC_API_LENDING_SPROUT_SUBGRAPH_SERVER'),
      });

      const lendingHarvestSubgraphLink = createHttpLink({
        headers: getSubgraphHeaders(),
        uri: getEnv('NEXT_PUBLIC_API_LENDING_HARVEST_SUBGRAPH_SERVER'),
      });

      const errorLink = onError(({ graphQLErrors }) => {
        graphQLErrors?.forEach((err) => {
          const messageI18n = (err.extensions?.localizedMessages as Record<string, string>)?.[
            i18n.language
          ];
          if (messageI18n) {
            // Use Object.defineProperty to override read-only message
            Object.defineProperty(err, 'message', { value: messageI18n, writable: true });
          }
        });
      });

      const link = split(
        ({ operationName }) => operationName?.toLowerCase().startsWith('seed'),
        lendingSeedSubgraphLink,
        split(
          ({ operationName }) => operationName?.toLowerCase().startsWith('sprout'),
          lendingSproutSubgraphLink,
          split(
            ({ operationName }) => operationName?.toLowerCase().startsWith('harvest'),
            lendingHarvestSubgraphLink,
            eip721Link,
          ),
        ),
      );

      return new ApolloClient({
        link: from([errorLink, link]),
        cache,
      });
    }, [i18n.language]);

    return (
      <ApolloProvider client={client}>
        <ChildComponent {...props} />
      </ApolloProvider>
    );
  };

  WithApolloClientHOC.displayName = 'WithApolloClientHOC';
  hoistNonReactStatics(WithApolloClientHOC, ChildComponent);

  return WithApolloClientHOC;
};
